import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/CompanyDescriptionExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialNeedsExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_MARKET_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/MarketExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_PRODUCTS_OR_SERVICES_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/ProductsExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_RISKS_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/RisksExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES } from 'components/BusinessToolForm/Sections/ExecutiveSummary/TeamExecSummary/utils/form';

import { ol } from '../helpers';

const COMPANY_DESCRIPTION_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.HISTORY]: {
    name: 'Geschichte des Unternehmens',
    instruction:
      'Stellen Sie kurz die Gründung Ihres Unternehmens und frühe Meilensteine vor, einschließlich der Marktlücke, die zur Gründung geführt hat. Heben Sie erste Erfolge wie Unternehmensregistrierung, Lizenzerteilung oder Prototypentwicklung hervor.',
    exampleOne:
      'Im Jahr 2020 brachte das Unternehmen sein erstes Kettentraktormodell auf den Markt und revolutionierte damit die Landwirtschaft auf schwierigen Böden durch überlegene Traktion und Auftrieb.',
    exampleTwo:
      'Im Jahr 2021 führte die Sprachplattform ihre KI-gestützte Übersetzungsfunktion ein und revolutionierte damit die mehrsprachige Kommunikation.',
  },
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.RESOURCES_AND_ASSETS]: {
    name: 'Wesentliche Ressourcen und Vermögenswerte',
    instruction:
      'Kategorisieren Sie die Vermögenswerte des Unternehmens: physische Einrichtungen, Ausrüstung, Lagerbestände sowie immaterielle Vermögenswerte wie Technologie, geistiges Eigentum und Markenwert; bewerten Sie diese kurz.',
    exampleOne:
      'Die wichtigsten Ressourcen und Vermögenswerte des Unternehmens umfassen hochmoderne Produktionsanlagen, Spitzentechnologie und Maschinen, eine qualifizierte Belegschaft, geistige Eigentumsrechte und strategische Partnerschaften. Die Produktionsanlagen des Unternehmens verfügen über fortschrittliche Fertigungslinien und Qualitätskontrollsysteme, die eine effiziente Herstellung hochwertiger Landmaschinen ermöglichen.',
    exampleTwo:
      'Die wichtigsten Ressourcen und Vermögenswerte des Unternehmens umfassen fortschrittliche KI-Technologie, ein vielfältiges Netzwerk qualifizierter Übersetzer, robuste Sicherheitsprotokolle, Kundenbeziehungen und geistige Eigentumsrechte.',
  },
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.COMPANY_OWNERS]: {
    name: 'Eigentümer des Unternehmens',
    instruction:
      'Geben Sie die vollständigen gesetzlichen Namen der Unternehmenseigentümer an. Überprüfen Sie Schreibweise und Format sorgfältig, um Fehler zu vermeiden.',
    exampleOne: '1. John Johnson \n 2. Oliver Smith',
    exampleTwo: '1. John Doe',
  },
};

const PRODUCTS_OR_SERVICES_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_PRODUCTS_OR_SERVICES_NAMES.PRODUCT_VALUE]: {
    name: 'Wert des Produkts/der Dienstleistung und gelöstes Problem',
    instruction:
      'Formulieren Sie klar das zentrale Wertversprechen Ihres Produkts bzw. Ihrer Dienstleistung und deren Problemlösungsbeitrag. Erläutern Sie die Vorteile für Kunden und die praktische Wirkung.',
    exampleOne:
      'Durch die Minimierung der Bodenverdichtung und Maximierung der Traktion ermöglichen die Kettentraktoren den Landwirten, effizienter zu arbeiten und Aufgaben schneller und präziser zu erledigen.',
    exampleTwo:
      'Die Plattform für Sprachdienstleistungen ermöglicht reibungslose Kommunikation über Sprachbarrieren hinweg und hilft Kunden, effektiv mit globalen Zielgruppen zu interagieren und ihre Reichweite zu vergrößern.',
  },
};

const MARKET_AND_CUSTOMERS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_MARKET_NAMES.DYNAMICS]: {
    name: 'Marktdynamik',
    instruction:
      'Skizzieren Sie die Marktdynamik: Kräfte, die Angebot, Nachfrage, Wettbewerb und Branchenstruktur beeinflussen. Identifizieren Sie die CAGR des Zielmarktes. Gehen Sie auf aktuelle Trends ein: Veränderungen in Verbraucherpräferenzen, Technologie, Best Practices. Heben Sie die Auswirkungen auf Ihr Unternehmen und dessen Anpassungsfähigkeit hervor.',
    exampleOne:
      'In den letzten zehn Jahren verzeichnete der Markt eine durchschnittliche jährliche Wachstumsrate (CAGR) von etwa 5 % und erreichte 2020 ein Gesamtvolumen von über 200 Milliarden USD. Dieses Wachstum wird durch zunehmende Mechanisierung in Entwicklungsländern angetrieben, in denen moderne landwirtschaftliche Praktiken zur Deckung der steigenden Nahrungsmittelnachfrage verstärkt eingeführt werden.',
    exampleTwo:
      'Der Markt für Sprachdienstleistungen wächst stark, angetrieben durch Globalisierung, Digitalisierung und zunehmende grenzüberschreitende Kommunikation. Wichtige Trends sind die steigende Nachfrage nach KI-gestützten Übersetzungslösungen, das Wachstum in aufstrebenden Märkten, die Einführung cloudbasierter Sprachplattformen sowie der Fokus auf Sprachvielfalt und Lokalisierung.',
  },
  [EXECUTIVE_SUMMARY_MARKET_NAMES.COMPETITION_PLAN]: {
    name: 'Wettbewerbsstrategie',
    instruction:
      'Stellen Sie Ihre Wettbewerbsstrategie vor: wie Sie Ihr Angebot von Wettbewerbern abheben. Heben Sie Ihre Unique Selling Propositions hervor, z. B. Innovation, Preisgestaltung, Kundenerlebnis. Beschreiben Sie Ihre Marktpositionierung und die Leitprinzipien.',
    exampleOne:
      'Das Unternehmen investiert stark in Forschung und Entwicklung, um das Produktportfolio kontinuierlich zu verbessern, technologische Führungsposition zu sichern und sich an veränderte Kundenbedürfnisse anzupassen. Das Unternehmen priorisiert Kundenzufriedenheit durch exzellenten Service und Unterstützung über den gesamten Kundenlebenszyklus hinweg.',
    exampleTwo:
      'Das Unternehmen plant, sich durch Innovation, überlegene Technologie, personalisierten Service und Agilität zu differenzieren. Durch kontinuierliche Verbesserung der KI-Technologie, Ausbau der Sprachunterstützung und herausragende Kundenerlebnisse will das Unternehmen der Konkurrenz voraus bleiben und seine Position als Marktführer in der Sprachdienstleistungsbranche behaupten.',
  },
};

const ORGANIZATION_AND_TEAM_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS]: {
    name: 'Kernteam',
    instruction: 'Listen Sie die Kernmitglieder des Teams auf.',
    exampleOne:
      'Zu den wichtigsten Projektbeteiligten im Unternehmen gehören John Johnson, Oliver Smith, Isabella Brown und Henry Miller.',
    exampleTwo:
      'Zu den wichtigsten Projektbeteiligten im Unternehmen gehören John Johnson, Mary Wilson und Liam Anderson.',
  },
};

const FINANCIAL_INFORMATION_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.AVERAGE_ANNUAL_PROFIT]: {
    name: 'Vergangener Jahresgewinn',
    instruction:
      'Berechnen Sie den durchschnittlichen Jahresgewinn, indem Sie den Nettogewinn der letzten drei Jahre addieren und durch drei teilen. Diese Kennzahl sollte korrekt sein und die finanzielle Performance über den Zeitraum konsistent widerspiegeln.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.ASSETS_SIZE]: {
    name: 'Größe Ihres Vermögens',
    instruction:
      'Zur Berechnung der Größe Ihres Vermögens:' +
      ol([
        'Listen Sie alle Vermögenswerte auf, einschließlich Bargeld, Investitionen, Immobilien und Ausrüstung.',
        'Ermitteln Sie den aktuellen Marktwert jedes Vermögenswertes.',
        'Addieren Sie die Werte, um die Gesamtgröße des Vermögens zu erhalten.',
      ]) +
      'Verwenden Sie die Formel: Gesamtvermögen = Bargeld + Investitionen + Immobilien + Ausrüstung + sonstige Vermögenswerte.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.PROJECTED_ANNUAL_PROFIT]: {
    name: 'Prognostizierter Jahresgewinn',
    instruction:
      'Zur Berechnung des prognostizierten Jahresgewinns für die nächsten drei Jahre:' +
      ol([
        'Prognostizieren Sie den Jahresumsatz auf Basis von Verkaufs- und Preistrends.',
        'Subtrahieren Sie die Herstellungskosten (COGS), um den Bruttogewinn zu ermitteln.',
        'Ziehen Sie Betriebsaufwendungen, Steuern und Zinsen ab.',
        'Berücksichtigen Sie erwartetes Wachstum, Marktbedingungen sowie geplante Investitionen oder Kostenänderungen.',
      ]),
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.RETURN_ON_INVESTMENT]: {
    name: 'Kapitalrendite (ROI)',
    instruction:
      'Zur Berechnung der Kapitalrendite (Return on Investment, ROI):' +
      ol([
        'Ermitteln Sie den Nettogewinn aus der Investition.',
        'Teilen Sie den Nettogewinn durch die anfänglichen Investitionskosten.',
        'Multiplizieren Sie das Ergebnis mit 100, um den Wert in Prozent auszudrücken.',
      ]) +
      'Verwenden Sie die Formel: ROI = (Nettogewinn / anfängliche Investitionskosten) * 100.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.INTERNAL_RATE_OF_RETURN]: {
    name: 'Interner Zinsfuß (IRR)',
    instruction:
      'Zur Berechnung des Internen Zinsfußes (Internal Rate of Return, IRR):' +
      ol([
        'Sammeln Sie Daten zur Anfangsinvestition und zu den erwarteten Zahlungsströmen (Cashflows).',
        'Bestimmen Sie den Zinssatz, bei dem der Kapitalwert (Net Present Value, NPV) der Cashflows gleich null ist.',
        'Nutzen Sie Finanzsoftware, um den IRR zu berechnen.',
      ]),
    exampleOne: '',
    exampleTwo: '',
  },
};

const FUNDING_DIRECTIONS_INSTRUCTIONS = {
  name: 'Verwendungszwecke der Finanzierung',
  instruction:
    'Geben Sie die Budgetzuordnung für jeden Zweck an: F&E, Marketing, Betriebskapital, Bau/Immobilien, Ausrüstung.',
  exampleOne: '',
  exampleTwo: '',
};

const FINANCING_NEEDS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.RESEARCH_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.MARKETING_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.WORKING_CAPITAL_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.CONSTRUCTION_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.EQUIPMENT_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.OTHER_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
};

const RISKS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_RISKS_NAMES.MAIN_RISKS]: {
    name: 'Wichtigste Projektrisiken',
    instruction:
      'Skizzieren Sie die wichtigsten Projektrisiken: Marktdynamik, technologische Herausforderungen, regulatorische Änderungen, finanzielle Unsicherheiten. Bewerten Sie die potenziellen Auswirkungen jedes Risikos auf den Projekterfolg.',
    exampleOne: ol([
      'Unerwartete technische Komplexitäten während des Entwicklungsprozesses.',
      'Marktvolatilität und sich ändernde Kundenpräferenzen stellen ein weiteres Risiko dar, da Veränderungen der wirtschaftlichen Rahmenbedingungen, der regulatorischen Politik oder der Wettbewerbssituation die Nachfrage nach dem Produkt beeinflussen oder die Marktdynamik verändern könnten.',
    ]),
    exampleTwo:
      'Die wichtigsten Risiken für unser Projekt umfassen technologische Umbrüche, Cyberbedrohungen, Marktvolatilität, regulatorische Änderungen und Wettbewerb. Darüber hinaus können die Abhängigkeit von externen Anbietern oder Fachkräftemangel Herausforderungen darstellen. ',
  },
  [EXECUTIVE_SUMMARY_RISKS_NAMES.MEASURES]: {
    name: 'Risikominderungsmaßnahmen',
    instruction:
      'Beschreiben Sie Maßnahmen zur Risikominderung: Diversifizierung, Versicherungen, Rücklagen, Partnerschaften. Heben Sie laufende Risikoüberwachung und Anpassungsfähigkeit des Teams hervor. Unterstreichen Sie die Wirksamkeit Ihrer Risikomanagementstrategie.',
    exampleOne: ol([
      'Das Unternehmen setzt auf ein robustes Risikomanagement, führt gründliche Marktforschung durch und pflegt offene Kommunikationskanäle mit Stakeholdern, um potenzielle Probleme frühzeitig zu erkennen und proaktiv gegenzusteuern.',
      'Das Unternehmen hält Notfallpläne bereit und bleibt flexibel in der Projektumsetzung, um sich an veränderte Rahmenbedingungen anzupassen und den Projekterfolg zu sichern.',
    ]),
    exampleTwo:
      'Zu den Risikominderungsstrategien gehören unter anderem:' +
      ol([
        'Diversifizierung der Erlösquellen',
        'Kontinuierliche Beobachtung von Branchentrends',
        'Aufrechterhaltung robuster Sicherheitsmaßnahmen',
      ]),
  },
};

export const EXECUTIVE_SUMMARY_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.COMPANY_DESCRIPTION]: COMPANY_DESCRIPTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.PRODUCTS_OR_SERVICES]: PRODUCTS_OR_SERVICES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.MARKET_AND_CUSTOMERS]: MARKET_AND_CUSTOMERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.ORGANIZATION_AND_TEAM]: ORGANIZATION_AND_TEAM_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCIAL_INFORMATION]: FINANCIAL_INFORMATION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCING_NEEDS]: FINANCING_NEEDS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.RISKS]: RISKS_INSTRUCTIONS,
};
